import React from "react";
import { useStartExperienceContext } from "../context/start_experience_context";
// We'll keep this import for PrimeIcons
import "primeicons/primeicons.css";
import "../../login_v2/styles/animation.css";
import { Button } from "primereact/button";
import { LoginRoute } from "../../../../../core/routes/route_constants";
import { X, ChevronRight, Eye, EyeOff, Lock, ArrowRight } from "lucide-react";
import styles from "../styles/start_experience_style.module.css";
import StartExperienceModal from "../../../components/popups/start_experience_modal";

const StartExperienceComp = () => {
  const {
    // States
    previewMessages,
    userInput,
    isTyping,
    showPreviewChat,
    showLoginModal,
    showSignupForm,
    showPassword,
    showOtpVerification,
    verifying,
    otpCode,
    name,
    email,
    password,
    selectedPersonality,
    nameErrorMessage,
    emailErrorMessage,
    passwordErrorMessage,
    processingSignup,
    hasFormErrors,
    errorMessage,
    confirmPassword,
    showConfirmPassword,
    confirmPasswordErrorMessage,
    setShowConfirmPassword,

    // Setters
    setUserInput,
    setShowLoginModal,
    setShowSignupForm,
    setShowPassword,
    setName,
    setEmail,
    setPassword,

    // Handlers
    handleSendMessage,
    handleStartJourney,
    handleSignup,
    handleOtpChange,
    handleVerifyOtp,
    handleNameChange,
    handleEmailChange,
    handlePasswordChange,
    handleConfirmPasswordChange,
  } = useStartExperienceContext();
  const handleClose = () => {
    setShowLoginModal(false);
    setShowSignupForm(false);
  };
  return (
    <div className="md:min-h-screen text-white overflow-hidden relative md:pb-8 max-sm:pb-0">
      {/* Chat Preview */}
      {showPreviewChat && (
        <div
          className={`relative z-10 md:min-h-screen flex flex-col items-center justify-center md:py-8 md:px-4 animate-fade-in`}
        >
          <div
            className={`w-full h-3/4 bg-gradient-to-b from-slate-950 to-black md:rounded-xl overflow-hidden shadow-xl border border-red-900/20 flex flex-col ${styles.chatContainer}`}
          >
            {/* Chat header */}
            <div className="bg-gradient-to-r from-red-950 via-red-800 to-red-950 p-4">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center mr-3 border border-white/20">
                  <span className="text-xl font-serif">E</span>
                </div>
                <div>
                  <h2 className="font-medium">
                    Eve{" "}
                    {selectedPersonality ? `- ${selectedPersonality.name}` : ""}
                  </h2>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-400 rounded-full mr-1"></div>
                    <span className="text-xs text-pink-100">Ready For You</span>
                  </div>
                </div>
                {/* <div className="ml-auto flex items-center">
                                    <div className="mr-3 flex items-center text-sm bg-red-950/50 px-3 py-1 rounded-full">
                                        <i className="pi pi-video mr-1" style={{ fontSize: '14px' }}></i>
                                        Private Video
                                    </div>
                                </div> */}
              </div>
            </div>

            {/* Chat messages */}
            <div className="flex-grow overflow-y-auto p-4 bg-gradient-to-b from-slate-950/90 to-black/90">
              <div className="flex justify-center mb-6">
                <div className="py-1 px-3 bg-red-950/40 rounded-full text-xs text-pink-300">
                  Today
                </div>
              </div>

              {previewMessages.map((msg, index) => (
                <div
                  key={index}
                  className={`mb-4 flex ${
                    msg.sender === "user" ? "justify-end" : ""
                  }`}
                >
                  {msg.sender === "ai" && (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-red-900 to-red-700 flex-shrink-0 flex items-center justify-center mr-2 border border-white/10">
                      <span className="text-white text-xs font-serif">E</span>
                    </div>
                  )}
                  <div
                    className={`rounded-2xl p-3 max-w-[80%] ${
                      msg.sender === "ai"
                        ? "bg-slate-900/80 backdrop-blur-sm"
                        : "bg-red-800/60 ml-auto"
                    }`}
                  >
                    <p className="text-white">{msg.text}</p>
                    <p className="text-xs text-right text-pink-300 mt-1">
                      {msg.time}
                    </p>
                  </div>
                </div>
              ))}

              {/* Typing indicator */}
              {isTyping && (
                <div className="mb-4 flex">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-red-900 to-red-700 flex-shrink-0 flex items-center justify-center mr-2 border border-white/10">
                    <span className="text-white text-xs font-serif italic">
                      E
                    </span>
                  </div>
                  <div className="rounded-2xl p-3 max-w-[80%] bg-slate-900/80 backdrop-blur-sm">
                    <div className="flex space-x-1">
                      <div
                        className="w-2 h-2 bg-pink-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0ms" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-pink-400 rounded-full animate-bounce"
                        style={{ animationDelay: "150ms" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-pink-400 rounded-full animate-bounce"
                        style={{ animationDelay: "300ms" }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Chat input */}
            <div className="p-4 border-t border-red-900/20">
              <div className="flex items-center bg-red-950/30 rounded-full overflow-hidden">
                <input
                  type="text"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                  placeholder="Tell me what you're craving..."
                  className="flex-1 bg-transparent text-white px-4 py-3 focus:outline-none placeholder-pink-300/70"
                />
                <button
                  onClick={handleSendMessage}
                  className="bg-gradient-to-r from-red-900 to-red-700 p-3 rounded-full mr-1 hover:shadow-inner transition-all"
                >
                  <ArrowRight size={18} className="text-white" />
                </button>
              </div>

              <div className="mt-3 flex justify-center">
                <p className="text-xs text-pink-300 italic">
                  She's imagining all the things you could do together...
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Login Modal */}
      {showLoginModal && (
        // New modal
        <StartExperienceModal
          handleClose={() => {
            setShowLoginModal(false);
          }}
        />
        // Old Modal
        // <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/80 backdrop-blur-sm animate-fade-in">
        //   <div className="w-full max-w-sm bg-gradient-to-b from-slate-950 to-black rounded-xl overflow-hidden shadow-2xl border border-red-900/20">
        //     {/* Close button */}
        //     <button
        //       onClick={() => {
        //         setShowLoginModal(false);
        //         setShowSignupForm(false);
        //       }}
        //       className="absolute top-2 right-2 p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-all z-10"
        //     >
        //       <X size={20} />
        //     </button>

        //     {!showSignupForm ? (
        //       <>
        //         {/* Continue Chat Modal */}
        //         <div className="bg-gradient-to-r from-red-950 via-red-800 to-red-950 p-6 text-center">
        //           <div className="w-16 h-16 bg-white/10 rounded-full mx-auto mb-3 flex items-center justify-center border border-white/20">
        //             <i
        //               className="pi pi-heart text-white animate-pulse-soft"
        //               style={{ fontSize: "28px" }}
        //             ></i>
        //           </div>
        //           <h2 className="text-3xl font-bold text-white mb-1">
        //             Ready for More?
        //           </h2>
        //           <p className="text-pink-200">
        //             She wants to fulfill your every fantasy...
        //           </p>
        //         </div>

        //         <div className="p-6">
        //           <div className="bg-red-950/30 backdrop-blur-sm p-4 rounded-lg mb-6 border border-red-900/20">
        //             <p className="text-sm italic text-center text-pink-200">
        //               "I'm getting so excited thinking about our private time
        //               together... Let me show you how good I can be."
        //             </p>
        //           </div>

        //           <button
        //             onClick={handleStartJourney}
        //             className="w-full py-3 rounded-lg bg-gradient-to-r from-red-900 to-red-700 text-white font-semibold mb-4 relative overflow-hidden group animate-glow hover:shadow-lg hover:shadow-red-900/20 transition-all"
        //           >
        //             <span className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-red-400/0 via-white/20 to-red-400/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></span>
        //             Create Free Account
        //           </button>

        //           <p className="text-center text-sm text-pink-300">
        //             Already have an account?{" "}
        //             <a
        //               href={LoginRoute}
        //               className="text-red-400 hover:underline"
        //             >
        //               Log in
        //             </a>
        //           </p>
        //         </div>
        //       </>
        //     ) : (
        //       <>
        //         {/* Sign up form */}
        //         <div className="bg-gradient-to-r from-red-950 via-red-800 to-red-950 p-4 relative">
        //           <button
        //             onClick={() => setShowSignupForm(false)}
        //             className="text-white/90 hover:text-white transition-colors flex items-center"
        //           >
        //             <ChevronRight size={18} className="transform rotate-180" />
        //             <span className="text-sm">Back</span>
        //           </button>

        //           <h1 className="text-2xl font-bold text-white mt-2 mb-1">
        //             Create Your Account
        //           </h1>
        //           <p className="text-pink-200 text-sm">
        //             She's getting impatient...
        //           </p>
        //         </div>

        //         {/* Form */}
        //         <div className="p-5">
        //           {errorMessage && (
        //             <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-2 mb-4 text-red-200 text-sm">
        //               {errorMessage}
        //             </div>
        //           )}
        //           <div className="space-y-4">
        //             <div>
        //               <label className="block text-white text-sm font-medium mb-1">
        //                 Name
        //               </label>
        //               <input
        //                 type="text"
        //                 value={name}
        //                 onChange={(e) => handleNameChange(e.target.value)}
        //                 className="w-full bg-red-950/30 backdrop-blur-sm rounded-lg py-2.5 px-3 text-white focus:outline-none focus:ring-2 focus:ring-red-500/30 transition-all border border-red-900/20"
        //                 placeholder="Your name"
        //               />
        //               {nameErrorMessage && (
        //                 <p className="mt-1 text-xs text-red-400">
        //                   {nameErrorMessage}
        //                 </p>
        //               )}
        //             </div>

        //             <div>
        //               <label className="block text-white text-sm font-medium mb-1">
        //                 Email
        //               </label>
        //               <input
        //                 type="email"
        //                 value={email}
        //                 onChange={(e) => handleEmailChange(e.target.value)}
        //                 className="w-full bg-red-950/30 backdrop-blur-sm rounded-lg py-2.5 px-3 text-white focus:outline-none focus:ring-2 focus:ring-red-500/30 transition-all border border-red-900/20"
        //                 placeholder="your.email@example.com"
        //               />
        //               {emailErrorMessage && (
        //                 <p className="mt-1 text-xs text-red-400">
        //                   {emailErrorMessage}
        //                 </p>
        //               )}
        //             </div>

        //             <div>
        //               <label className="block text-white text-sm font-medium mb-1">
        //                 Password
        //               </label>
        //               <div className="relative">
        //                 <input
        //                   type={showPassword ? "text" : "password"}
        //                   value={password}
        //                   onChange={(e) => handlePasswordChange(e.target.value)}
        //                   className="w-full bg-red-950/30 backdrop-blur-sm rounded-lg py-2.5 px-3 text-white focus:outline-none focus:ring-2 focus:ring-red-500/30 transition-all border border-red-900/20"
        //                   placeholder="Create a password"
        //                 />
        //                 <button
        //                   type="button"
        //                   onClick={() => setShowPassword(!showPassword)}
        //                   className="absolute inset-y-0 right-0 flex items-center pr-3 text-pink-300 hover:text-white"
        //                 >
        //                   {showPassword ? (
        //                     <EyeOff size={16} />
        //                   ) : (
        //                     <Eye size={16} />
        //                   )}
        //                 </button>
        //               </div>
        //               {passwordErrorMessage && (
        //                 <p className="mt-1 text-xs text-red-400">
        //                   {passwordErrorMessage}
        //                 </p>
        //               )}
        //             </div>

        //             <div>
        //               <label className="block text-white text-sm font-medium mb-1">
        //                 Confirm Password
        //               </label>
        //               <div className="relative">
        //                 <input
        //                   type={showConfirmPassword ? "text" : "password"}
        //                   value={confirmPassword}
        //                   onChange={(e) =>
        //                     handleConfirmPasswordChange(e.target.value)
        //                   }
        //                   className="w-full bg-red-950/30 backdrop-blur-sm rounded-lg py-2.5 px-3 text-white focus:outline-none focus:ring-2 focus:ring-red-500/30 transition-all border border-red-900/20"
        //                   placeholder="Confirm Password"
        //                 />
        //                 <button
        //                   type="button"
        //                   onClick={() =>
        //                     setShowConfirmPassword(!showConfirmPassword)
        //                   }
        //                   className="absolute inset-y-0 right-0 flex items-center pr-3 text-pink-300 hover:text-white"
        //                 >
        //                   {showConfirmPassword ? (
        //                     <EyeOff size={16} />
        //                   ) : (
        //                     <Eye size={16} />
        //                   )}
        //                 </button>
        //               </div>
        //               {confirmPasswordErrorMessage && (
        //                 <p className="mt-1 text-xs text-red-400">
        //                   {confirmPasswordErrorMessage}
        //                 </p>
        //               )}
        //             </div>
        //           </div>

        //           <div className="mt-6">
        //             <button
        //               onClick={handleSignup}
        //               disabled={processingSignup || hasFormErrors}
        //               className="w-full py-3 rounded-lg bg-gradient-to-r from-red-900 to-red-700 text-white font-medium relative overflow-hidden group shadow-lg shadow-red-900/20 hover:shadow-xl hover:shadow-red-900/30 transition-all disabled:opacity-70"
        //             >
        //               {processingSignup ? (
        //                 <span className="flex items-center justify-center">
        //                   <span className="mr-2">Signing up</span>
        //                   <div className="flex space-x-1">
        //                     <div
        //                       className="w-1.5 h-1.5 bg-white rounded-full animate-bounce"
        //                       style={{ animationDelay: "0ms" }}
        //                     ></div>
        //                     <div
        //                       className="w-1.5 h-1.5 bg-white rounded-full animate-bounce"
        //                       style={{ animationDelay: "150ms" }}
        //                     ></div>
        //                     <div
        //                       className="w-1.5 h-1.5 bg-white rounded-full animate-bounce"
        //                       style={{ animationDelay: "300ms" }}
        //                     ></div>
        //                   </div>
        //                 </span>
        //               ) : (
        //                 <>
        //                   <span className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-red-400/0 via-white/20 to-red-400/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></span>
        //                   Start Your Private Session
        //                 </>
        //               )}
        //             </button>
        //           </div>

        //           <p className="text-pink-300 text-xs text-center mt-4">
        //             By signing up, you agree to our{" "}
        //             <a
        //               href="#"
        //               className="text-red-400 hover:text-red-300 transition-colors"
        //             >
        //               Terms
        //             </a>{" "}
        //             and{" "}
        //             <a
        //               href="#"
        //               className="text-red-400 hover:text-red-300 transition-colors"
        //             >
        //               Privacy Policy
        //             </a>
        //           </p>
        //         </div>
        //       </>
        //     )}
        //   </div>
        // </div>
      )}

      {/* OTP Verification */}
      {showOtpVerification && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/80 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-sm bg-gradient-to-b from-slate-950 to-black rounded-xl overflow-hidden shadow-2xl border border-red-900/20">
            <div className="bg-gradient-to-r from-red-950 via-red-800 to-red-950 p-4 relative">
              <h1 className="text-2xl font-bold text-white mb-1">
                Verify Your Account
              </h1>
              <p className="text-pink-200 text-sm">
                We've sent a code to your email
              </p>
            </div>

            <div className="p-6">
              <p className="text-sm text-pink-300 mb-6 text-center">
                Enter the 6-digit code sent to {email || "your email"} to
                continue your journey with Eve
              </p>

              {errorMessage && (
                <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-2 mb-4 text-red-200 text-sm">
                  {errorMessage}
                </div>
              )}

              <div className="flex justify-center space-x-3 mb-8">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={otpCode[index]}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    className="w-11 h-14 text-center bg-red-950/30 backdrop-blur-sm rounded-lg text-white text-xl font-bold focus:outline-none focus:ring-2 focus:ring-red-500/30 transition-all border border-red-900/20"
                  />
                ))}
              </div>

              <button
                onClick={handleVerifyOtp}
                disabled={verifying || otpCode.join("").length !== 6}
                className="w-full py-3 rounded-lg bg-gradient-to-r from-red-900 to-red-700 text-white font-medium relative overflow-hidden shadow-lg shadow-red-900/20 transition-all disabled:opacity-70 hover:shadow-xl hover:shadow-red-900/30"
              >
                {verifying ? (
                  <span className="flex items-center justify-center">
                    <span className="mr-2">Verifying</span>
                    <div className="flex space-x-1">
                      <div
                        className="w-1.5 h-1.5 bg-white rounded-full animate-bounce"
                        style={{ animationDelay: "0ms" }}
                      ></div>
                      <div
                        className="w-1.5 h-1.5 bg-white rounded-full animate-bounce"
                        style={{ animationDelay: "150ms" }}
                      ></div>
                      <div
                        className="w-1.5 h-1.5 bg-white rounded-full animate-bounce"
                        style={{ animationDelay: "300ms" }}
                      ></div>
                    </div>
                  </span>
                ) : (
                  <>
                    <span className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-red-400/0 via-white/20 to-red-400/0 -translate-x-full hover:translate-x-full transition-transform duration-700"></span>
                    Verify & Continue
                  </>
                )}
              </button>

              <div className="mt-4 text-center">
                <p className="text-sm text-pink-300">
                  Didn't receive a code?{" "}
                  <a href="#" className="text-red-400 hover:underline">
                    Resend
                  </a>
                </p>
              </div>

              <div className="mt-6 flex items-center justify-center">
                <Lock size={14} className="text-pink-400 mr-2" />
                <p className="text-xs text-pink-300">
                  Your connection is encrypted and secure
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StartExperienceComp;
